import React, { useState, useRef, useEffect } from 'react';
import { Printer, Download, ArrowLeft, Edit3, Eye, Plus, Trash2, Loader2 } from 'lucide-react';
import { useLocation } from 'react-router-dom';

const ComparativeStatementReport = () => {
  const printRef = useRef();
  const [isPreviewMode, setIsPreviewMode] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [reportData, setReportData] = useState({
    company: {
      name: "Nissi Infotech"
    },
    header: {
      comparisonNo: "",
      purchaseRequestDate: "",
      comparisonDate: "",
      purchaseRequestNo: "",
      requestedFor: ""
    },
    vendors: [],
    signatures: {
      preparedBy: "",
      checkedBy: "",
      approvedBy: "",
      date: ""
    },
    terms: {
      priceBasis: "",
      validity: "",
      termsOfPayment: ""
    }
  });

   const location = useLocation();
   // ✅ Extract id from query string
  const queryParams = new URLSearchParams(location.search);
  const id = queryParams.get("id");

  // Fetch data from API
  const fetchData = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);

      const res = await fetch(`https://www.nissi-india.com/api/grn_quotationPrint?ids=${id}`);
      const data = await res.json();
      console.log("API Response:", data);

      if (data.success && data.data && data.data.length > 0) {
        const apiData = data.data[0]; // Get first item from data array
        
        // Extract vendor names from s1, s2, s3, s4, s5 fields
        const vendors = [];
        for (let i = 1; i <= 5; i++) {
          const supplierName = apiData[`s${i}`];
          if (supplierName && supplierName.trim() !== "") {
            const vendor = {
              name: supplierName,
              items: [{
                sno: 1,
                description: apiData.fld_itemname || "",
                unit: apiData.fld_uomcode || "Nos",
                qty: apiData.fld_Quantity || 0,
                price: apiData[`s${i}price`] || 0,
                discount: apiData[`s${i}discount`] || 0,
                sgst: apiData[`s${i}vat`] || 0, // Using VAT as SGST
                cgst: apiData[`s${i}cst`] || 0, // Using CST as CGST
                igst: 0,
                total: calculateTotal(
                  apiData.fld_Quantity || 0,
                  apiData[`s${i}price`] || 0,
                  apiData[`s${i}discount`] || 0,
                  apiData[`s${i}vat`] || 0,
                  apiData[`s${i}cst`] || 0
                ),
                status: apiData[`s${i}approved`] === "Approved" ? "A" : "D/A"
              }],
              totals: calculateVendorTotals(i, apiData)
            };
            vendors.push(vendor);
          }
        }

        // Update report data
        setReportData(prev => ({
          ...prev,
          header: {
            comparisonNo: apiData.fld_QuotationNo || "",
            purchaseRequestDate: formatDate(apiData.fld_indentdate) || "",
            comparisonDate: formatDate(apiData.fld_QuotationDate) || "",
            purchaseRequestNo: apiData.fld_indentno || "",
            requestedFor: apiData.fld_req_for || ""
          },
          vendors: vendors,
          signatures: {
            preparedBy: apiData.fld_employee || "",
            checkedBy: "",
            approvedBy: apiData.fld_mdemployee || "",
            date: `${formatDate(apiData.fld_approveddate)} ${apiData.fld_approvedtime || ""}`
          }
        }));

      } else {
        setError("No data found for this comparison statement.");
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to fetch comparison statement details.");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to calculate item total
  const calculateTotal = (qty, price, discount, sgst, cgst) => {
    const subtotal = qty * price;
    const afterDiscount = subtotal - discount;
    const taxAmount = (afterDiscount * (sgst + cgst)) / 100;
    return afterDiscount + taxAmount;
  };

  // Helper function to calculate vendor totals
  const calculateVendorTotals = (vendorIndex, apiData) => {
    const qty = apiData.fld_Quantity || 0;
    const price = apiData[`s${vendorIndex}price`] || 0;
    const discount = apiData[`s${vendorIndex}discount`] || 0;
    const sgst = apiData[`s${vendorIndex}vat`] || 0;
    const cgst = apiData[`s${vendorIndex}cst`] || 0;
    const freight = apiData[`fr${vendorIndex}`] || 0;
    const insurance = apiData[`ins${vendorIndex}`] || 0;
    const pf = apiData[`pf${vendorIndex}`] || 0;
    const roundoff = apiData[`roundoff${vendorIndex}`] || 0;
    const tcs = apiData[`tcsamt${vendorIndex}`] || 0;

    const subTotal = qty * price;
    const afterDiscount = subTotal - discount;
    const sgstAmount = (afterDiscount * sgst) / 100;
    const cgstAmount = (afterDiscount * cgst) / 100;
    const grandTotal = afterDiscount + sgstAmount + cgstAmount + freight + insurance + pf + roundoff + tcs;

    return {
      subTotal: subTotal,
      discount: discount,
      afterDiscount: afterDiscount,
      cgst: cgstAmount,
      sgst: sgstAmount,
      igst: 0,
      pfOthers: pf,
      freight: freight,
      insurance: insurance,
      tcs: tcs,
      roundOff: roundoff,
      grandTotal: grandTotal
    };
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB'); // DD/MM/YYYY format
  };

  // Fetch data on component mount or when id changes
  useEffect(() => {
    fetchData();
  }, [id]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    console.log("Download PDF functionality");
  };

  const addVendor = () => {
    const newVendor = {
      name: "New Vendor",
      items: [
        {
          sno: 1,
          description: reportData.vendors[0]?.items[0]?.description || "",
          unit: reportData.vendors[0]?.items[0]?.unit || "SF",
          qty: reportData.vendors[0]?.items[0]?.qty || 0,
          price: 0.00,
          discount: 0.00,
          sgst: 0.00,
          cgst: 0.00,
          igst: 0.00,
          total: 0.00,
          status: ""
        }
      ],
      totals: {
        subTotal: 0.00,
        discount: 0.00,
        afterDiscount: 0.00,
        cgst: 0.00,
        sgst: 0.00,
        igst: 0.00,
        pfOthers: 0.00,
        freight: 0.00,
        insurance: 0.00,
        tcs: 0.00,
        roundOff: 0.00,
        grandTotal: 0.00
      }
    };
    setReportData(prev => ({
      ...prev,
      vendors: [...prev.vendors, newVendor]
    }));
  };

  const updateVendor = (vendorIndex, field, value) => {
    setReportData(prev => ({
      ...prev,
      vendors: prev.vendors.map((vendor, index) => 
        index === vendorIndex ? { ...vendor, [field]: value } : vendor
      )
    }));
  };

  const updateItem = (vendorIndex, itemIndex, field, value) => {
    setReportData(prev => ({
      ...prev,
      vendors: prev.vendors.map((vendor, vIndex) => 
        vIndex === vendorIndex 
          ? {
              ...vendor,
              items: vendor.items.map((item, iIndex) => 
                iIndex === itemIndex 
                  ? { 
                      ...item, 
                      [field]: value,
                      total: field === 'price' || field === 'qty' 
                        ? (field === 'price' ? value : item.price) * (field === 'qty' ? value : item.qty)
                        : item.total
                    }
                  : item
              )
            }
          : vendor
      )
    }));
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center gap-3">
          <Loader2 className="animate-spin" size={24} />
          <span className="text-lg">Loading comparison statement...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-lg mb-4">{error}</div>
          <button 
            onClick={fetchData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // No vendors found
  if (reportData.vendors.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-600 text-lg mb-4">No vendors found for this comparison statement.</div>
          <button 
            onClick={fetchData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Refresh
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 print:bg-white">
      {/* Header Controls - Hidden in print */}
      <div className="print:hidden bg-white shadow-sm border-b p-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <button className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors">
              <ArrowLeft size={18} />
              Back
            </button>
            <div className="flex items-center gap-3">
              <button
                onClick={addVendor}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg text-sm transition-colors"
              >
                <Plus size={16} />
                Add Vendor
              </button>
              <button
                onClick={() => setIsPreviewMode(!isPreviewMode)}
                className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 border border-blue-200 rounded-lg text-sm transition-colors"
              >
                {isPreviewMode ? <Edit3 size={16} /> : <Eye size={16} />}
                {isPreviewMode ? 'Edit Mode' : 'Preview Mode'}
              </button>
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white hover:bg-green-700 rounded-lg text-sm transition-colors"
              >
                <Download size={16} />
                Export PDF
              </button>
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white hover:bg-black rounded-lg text-sm transition-colors"
              >
                <Printer size={16} />
                Print Report
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Report Content */}
      <div className="max-w-7xl mx-auto print:max-w-full print:mx-0 p-6 print:p-0">
        <div
          ref={printRef}
          className="bg-white print:bg-white shadow-lg print:shadow-none border-2 border-black print:border-2 print:border-black rounded-lg print:rounded-none overflow-hidden"
        >
          {/* Report Header */}
          <div className="bg-black text-white print:bg-black print:text-white p-2 text-center">
            <h1 className="text-lg font-bold">Main Report</h1>
          </div>

          {/* Company Header */}
          <div className="border-b-2 border-black print:border-b-2 print:border-black p-4 text-center bg-gray-50 print:bg-gray-50">
            <h1 className="text-2xl font-bold text-red-600" style={{color: '#dc2626'}}>
              {reportData.company.name}
            </h1>
          </div>

          {/* Report Title */}
          <div className="border-b-2 border-black print:border-b-2 print:border-black p-3 text-center">
            <h2 className="text-lg font-bold text-black underline">Comparative Statement</h2>
          </div>

          {/* Header Details */}
          <div className="border-b-2 border-black print:border-b-2 print:border-black p-4">
            <div className="grid grid-cols-3 gap-6 text-sm">
              <div className="flex justify-between">
                <span className="font-semibold">Comparison No:</span>
                <span>{reportData.header.comparisonNo}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-semibold">Purchase Request Date:</span>
                <span>{reportData.header.purchaseRequestDate}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-semibold">Comparison Date:</span>
                <span>{reportData.header.comparisonDate}</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6 text-sm mt-2">
              <div className="flex justify-between">
                <span className="font-semibold">Purchase Request No:</span>
                <span>{reportData.header.purchaseRequestNo}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-semibold">Requested For:</span>
                <span>{reportData.header.requestedFor}</span>
              </div>
            </div>
          </div>

          {/* Comparison Table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr>
                  <th rowSpan="2" className="border-2 border-black bg-gray-100 print:bg-gray-100 px-2 py-2 text-center font-bold">S.No</th>
                  <th rowSpan="2" className="border-2 border-black bg-gray-100 print:bg-gray-100 px-2 py-2 text-center font-bold">Description of<br/>Item</th>
                  <th rowSpan="2" className="border-2 border-black bg-gray-100 print:bg-gray-100 px-2 py-2 text-center font-bold">Unit</th>
                  <th rowSpan="2" className="border-2 border-black bg-gray-100 print:bg-gray-100 px-2 py-2 text-center font-bold">Qty</th>
                  {reportData.vendors.map((vendor, index) => (
                    <th key={index} colSpan="7" className="border-2 border-black bg-gray-100 print:bg-gray-100 px-2 py-1 text-center font-bold">
                      {isPreviewMode ? (
                        vendor.name
                      ) : (
                        <input
                          type="text"
                          value={vendor.name}
                          onChange={(e) => updateVendor(index, 'name', e.target.value)}
                          className="w-full text-xs p-1 border border-gray-300 text-center font-bold"
                        />
                      )}
                    </th>
                  ))}
                </tr>
                <tr>
                  {reportData.vendors.map((vendor, index) => (
                    <React.Fragment key={index}>
                      <th className="border border-black bg-gray-50 print:bg-gray-50 px-1 py-1 text-center font-semibold">Make:</th>
                      <th className="border border-black bg-gray-50 print:bg-gray-50 px-1 py-1 text-center font-semibold">Make:</th>
                      <th className="border border-black bg-gray-50 print:bg-gray-50 px-1 py-1 text-center font-semibold">Price</th>
                      <th className="border border-black bg-gray-50 print:bg-gray-50 px-1 py-1 text-center font-semibold">Disc</th>
                      <th className="border border-black bg-gray-50 print:bg-gray-50 px-1 py-1 text-center font-semibold">SGST</th>
                      <th className="border border-black bg-gray-50 print:bg-gray-50 px-1 py-1 text-center font-semibold">CGST</th>
                      <th className="border border-black bg-gray-50 print:bg-gray-50 px-1 py-1 text-center font-semibold">IGST</th>
                      <th className="border border-black bg-gray-50 print:bg-gray-50 px-1 py-1 text-center font-semibold">Total</th>
                      <th className="border border-black bg-gray-50 print:bg-gray-50 px-1 py-1 text-center font-semibold">Status</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reportData.vendors[0]?.items.map((_, itemIndex) => (
                  <tr key={itemIndex}>
                    <td className="border border-black px-2 py-1 text-center font-semibold">
                      {itemIndex + 1}
                    </td>
                    <td className="border border-black px-2 py-1 font-medium">
                      {isPreviewMode ? (
                        reportData.vendors[0].items[itemIndex]?.description || ""
                      ) : (
                        <input
                          type="text"
                          value={reportData.vendors[0].items[itemIndex]?.description || ""}
                          onChange={(e) => {
                            // Update description for all vendors
                            reportData.vendors.forEach((_, vIndex) => {
                              updateItem(vIndex, itemIndex, 'description', e.target.value);
                            });
                          }}
                          className="w-full text-xs p-1 border border-gray-300"
                        />
                      )}
                    </td>
                    <td className="border border-black px-1 py-1 text-center">
                      {reportData.vendors[0].items[itemIndex]?.unit || ""}
                    </td>
                    <td className="border border-black px-1 py-1 text-center font-semibold">
                      {reportData.vendors[0].items[itemIndex]?.qty || ""}
                    </td>
                    {reportData.vendors.map((vendor, vendorIndex) => {
                      const item = vendor.items[itemIndex] || {};
                      return (
                        <React.Fragment key={vendorIndex}>
                          <td className="border border-black px-1 py-1 text-center">-</td>
                          <td className="border border-black px-1 py-1 text-center">-</td>
                          <td className="border border-black px-1 py-1 text-center">
                            {isPreviewMode ? (
                              `₹${item.price?.toFixed(2) || '0.00'}`
                            ) : (
                              <input
                                type="number"
                                value={item.price || 0}
                                onChange={(e) => updateItem(vendorIndex, itemIndex, 'price', parseFloat(e.target.value))}
                                className="w-full text-xs p-1 border border-gray-300 text-center"
                              />
                            )}
                          </td>
                          <td className="border border-black px-1 py-1 text-center">₹{item.discount?.toFixed(2) || '0.00'}</td>
                          <td className="border border-black px-1 py-1 text-center">₹{item.sgst?.toFixed(2) || '0.00'}</td>
                          <td className="border border-black px-1 py-1 text-center">₹{item.cgst?.toFixed(2) || '0.00'}</td>
                          <td className="border border-black px-1 py-1 text-center">₹{item.igst?.toFixed(2) || '0.00'}</td>
                          <td className="border border-black px-1 py-1 text-center font-bold">₹{item.total?.toFixed(2) || '0.00'}</td>
                          <td className="border border-black px-1 py-1 text-center font-semibold">
                            <span className={item.status === 'A' ? 'text-green-600' : 'text-red-600'}>
                              {item.status}
                            </span>
                          </td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals Section */}
          <div className="border-t-2 border-black print:border-t-2 print:border-black">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-xs">
                <tbody>
                  {[
                    { label: 'Sub Total:', field: 'subTotal' },
                    { label: 'Discount:', field: 'discount' },
                    { label: 'A.Dis Total:', field: 'afterDiscount' },
                    { label: 'CGST:', field: 'cgst' },
                    { label: 'SGST:', field: 'sgst' },
                    { label: 'IGST:', field: 'igst' }
                  ].map((row, index) => (
                    <tr key={index}>
                      <td className="border border-black px-2 py-1 font-semibold w-32">{row.label}</td>
                      <td className="border border-black px-2 py-1"></td>
                      <td className="border border-black px-2 py-1"></td>
                      <td className="border border-black px-2 py-1"></td>
                      {reportData.vendors.map((vendor, vendorIndex) => (
                        <React.Fragment key={vendorIndex}>
                          <td className="border border-black px-1 py-1"></td>
                          <td className="border border-black px-1 py-1"></td>
                          <td className="border border-black px-1 py-1 text-center">
                            ₹{vendor.totals[row.field]?.toFixed(2) || '0.00'}
                          </td>
                          <td className="border border-black px-1 py-1"></td>
                          <td className="border border-black px-1 py-1"></td>
                          <td className="border border-black px-1 py-1"></td>
                          <td className="border border-black px-1 py-1"></td>
                          <td className="border border-black px-1 py-1"></td>
                          <td className="border border-black px-1 py-1"></td>
                        </React.Fragment>
                      ))}
                    </tr>
                  ))}
                  
                  {/* Additional totals rows */}
                  {[
                    { label: 'PF / Others:', leftField: 'pfOthers', rightField: 'pfOthers' },
                    { label: 'Freight:', leftField: 'freight', rightField: 'freight' },
                    { label: 'Insurance:', leftField: 'insurance', rightField: 'insurance' },
                    { label: 'TCS(0.000%):', leftField: 'tcs', rightField: 'tcs' },
                    { label: 'Round Off (+/-):', leftField: 'roundOff', rightField: 'roundOff' }
                  ].map((row, index) => (
                    <tr key={`additional-${index}`}>
                      <td className="border border-black px-2 py-1"></td>
                      <td className="border border-black px-2 py-1"></td>
                      <td className="border border-black px-2 py-1"></td>
                      <td className="border border-black px-2 py-1"></td>
                      {reportData.vendors.map((vendor, vendorIndex) => (
                        <React.Fragment key={vendorIndex}>
                          <td className="border border-black px-1 py-1 font-semibold">{vendorIndex === 0 ? row.label : ''}</td>
                          <td className="border border-black px-1 py-1"></td>
                          <td className="border border-black px-1 py-1 text-center">
                            ₹{vendor.totals[row.leftField]?.toFixed(2) || '0.00'}
                          </td>
                          <td className="border border-black px-1 py-1 font-semibold">{vendorIndex === 1 ? row.label : ''}</td>
                          <td className="border border-black px-1 py-1"></td>
                          <td className="border border-black px-1 py-1 text-center">
                            ₹{vendor.totals[row.rightField]?.toFixed(2) || '0.00'}
                          </td>
                          <td className="border border-black px-1 py-1"></td>
                          <td className="border border-black px-1 py-1"></td>
                          <td className="border border-black px-1 py-1"></td>
                        </React.Fragment>
                      ))}
                    </tr>
                  ))}
                  
                  {/* Grand Total */}
                  <tr>
                    <td className="border border-black px-2 py-1"></td>
                    <td className="border border-black px-2 py-1"></td>
                    <td className="border border-black px-2 py-1"></td>
                    <td className="border border-black px-2 py-1"></td>
                    {reportData.vendors.map((vendor, vendorIndex) => (
                      <React.Fragment key={vendorIndex}>
                        <td className="border border-black px-1 py-1 font-bold">Grand Total:</td>
                        <td className="border border-black px-1 py-1"></td>
                        <td className="border border-black px-1 py-1 text-center font-bold">
                          ₹{vendor.totals.grandTotal?.toFixed(2) || '0.00'}
                        </td>
                        <td className="border border-black px-1 py-1 font-bold">Delivery</td>
                        <td className="border border-black px-1 py-1 font-bold">Grand Total:</td>
                        <td className="border border-black px-1 py-1 text-center font-bold">
                          ₹{vendor.totals.grandTotal?.toFixed(2) || '0.00'}
                        </td>
                        <td className="border border-black px-1 py-1"></td>
                        <td className="border border-black px-1 py-1"></td>
                        <td className="border border-black px-1 py-1"></td>
                      </React.Fragment>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Terms Section */}
          <div className="border-t-2 border-black print:border-t-2 print:border-black">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-xs">
                <tbody>
                  <tr>
                    <td className="border border-black px-2 py-1 font-semibold w-32">Price Basis</td>
                    <td className="border border-black px-2 py-1 font-semibold">Validity</td>
                    <td className="border border-black px-2 py-1 font-semibold">Terms of Payment</td>
                    <td className="border border-black px-2 py-1 font-semibold">Validity</td>
                    <td className="border border-black px-2 py-1 font-semibold">Terms of Payment</td>
                  </tr>
                  <tr>
                    <td className="border border-black px-2 py-3"></td>
                    <td className="border border-black px-2 py-3"></td>
                    <td className="border border-black px-2 py-3"></td>
                    <td className="border border-black px-2 py-3"></td>
                    <td className="border border-black px-2 py-3"></td>
                  </tr>
                  <tr>
                    <td className="border border-black px-2 py-1"></td>
                    <td className="border border-black px-2 py-1"></td>
                    <td className="border border-black px-2 py-1"></td>
                    <td className="border border-black px-2 py-1 font-semibold">Price Basis</td>
                    <td className="border border-black px-2 py-1 font-semibold">Approved by</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Signature Section */}
          <div className="border-t-2 border-black print:border-t-2 print:border-black p-4">
            <div className="grid grid-cols-3 gap-6 text-center">
              <div className="min-h-16">
                <h4 className="font-bold text-sm mb-2">Prepared by</h4>
                <div className="mt-8 text-xs">
                  {reportData.signatures.preparedBy}
                </div>
              </div>
              
              <div className="min-h-16">
                <h4 className="font-bold text-sm mb-2">Checked by</h4>
                <div className="mt-8 text-xs">
                  {reportData.signatures.checkedBy}
                </div>
                <div className="mt-4 text-xs font-semibold">
                  {reportData.signatures.date}
                </div>
              </div>
              
              <div className="min-h-16">
                <h4 className="font-bold text-sm mb-2">Approved by</h4>
                <div className="mt-8 text-xs">
                  {reportData.signatures.approvedBy}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t-2 border-black print:border-t-2 print:border-black bg-gray-100 print:bg-gray-100 p-3">
            <div className="flex justify-between items-center text-xs">
              <div>
                <p className="font-medium">Report generated by ERP-Software - www.infraconsoft.com owned by www.nissiinfotech.com</p>
              </div>
              <div>
                <p className="font-bold">Page 1 of 1</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          @page {
            margin: 0.5in;
            size: A4 landscape;
          }
          
          .print\\:hidden {
            display: none !important;
          }
          
          body {
            -webkit-print-color-adjust: exact;
            color-adjust: exact;
          }
          
          * {
            -webkit-print-color-adjust: exact;
            color-adjust: exact;
          }
          
          .shadow-lg {
            box-shadow: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ComparativeStatementReport;